import React, { useCallback, useEffect, useState } from "react";
import Breadcrumbs from "components/Common/Breadcrumb";
import axiosInstance from "Axiosinstance";
import * as XLSX from "xlsx";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import PoliciesTable from "partnerportal/components/PolicyTable";
import IssuePolicyModal from "partnerportal/components/IssuepolicyModal";
import {
  formatDate,
  getPolicyRowId,
  sanitizeFileName,
} from "partnerportal/helpers/utils";

const ITEMS_PER_PAGE = 30;
const TAG8_DEFAULT_COMPANY_UID = "tag8 default";

const todayISO = () => new Date().toISOString().split("T")[0];

const getLoggedInUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
};

const isSuperAdminUser = (user) =>
  user?.role === "superadmin" || user?.activitiesAccess?.includes("All");

// ── Inline debounce hook ──────────────────────────────────────────────────────
const useDebounce = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue; // plain value, NOT an array
};

const IssueBagProtectionPolicy = () => {
  const isSuperAdmin = isSuperAdminUser(getLoggedInUser());
  const listEndpoint = isSuperAdmin
    ? "/api/admin/issuedinsurancepolicies"
    : "/api/partner/issuedinsurancepolicies";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [issuedPolicies, setIssuedPolicies] = useState([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(true);
  const [resendingPolicyId, setResendingPolicyId] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [companyUid, setCompanyUid] = useState("");
  const [companies, setCompanies] = useState([]);

  // plain value — no array destructuring
  const debouncedSearch = useDebounce(searchTerm, 400);

  // ── Reset page when filters change ────────────────────────────────────────
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, fromDate, toDate, companyUid]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchCompanies = async () => {
      try {
        const { data } = await axiosInstance.get("/api/getcompanydetails");
        if (data?.status) {
          const list = (data.companies || [])
            .filter((company) => company.company_name && company.company_uid)
            .sort((a, b) =>
              String(a.company_name).localeCompare(String(b.company_name))
            );
          setCompanies(list);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchCompanies();
  }, [isSuperAdmin]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchIssuedPolicies = useCallback(
    async (page = 1) => {
      setIsLoadingPolicies(true);
      try {
        const params = new URLSearchParams({
          page,
          limit: ITEMS_PER_PAGE,
          ...(debouncedSearch?.trim() && { search: debouncedSearch.trim() }),
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
          ...(isSuperAdmin &&
            companyUid && { company_uid: companyUid }),
        });

        const { data } = await axiosInstance.get(`${listEndpoint}?${params}`);

        setIssuedPolicies(data?.issuedPolicies || []);
        setTotalPages(data?.pagination?.totalPages ?? 1);
        setTotalRecords(data?.pagination?.total ?? 0);
      } catch (error) {
        setIssuedPolicies([]);
        toast.error(
          error?.response?.data?.message ||
            "Unable to fetch issued bag protection policies",
          { position: "top-center" }
        );
      } finally {
        setIsLoadingPolicies(false);
      }
    },
    [debouncedSearch, fromDate, toDate, companyUid, isSuperAdmin, listEndpoint]
  );

  // Re-fetch whenever filters or page changes
  useEffect(() => {
    fetchIssuedPolicies(currentPage);
  }, [fetchIssuedPolicies, currentPage]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleModalSuccess = useCallback(
    async (message) => {
      toast.success(message, { position: "top-center" });
      await fetchIssuedPolicies(1);
    },
    [fetchIssuedPolicies]
  );

  const handleOpenCreateModal = useCallback(() => {
    setSelectedPolicy(null);
    setIsModalOpen(true);
  }, []);

  const handleToggleModal = useCallback(() => {
    setIsModalOpen((prev) => {
      const nextValue = !prev;
      if (!nextValue) {
        setSelectedPolicy(null);
      }
      return nextValue;
    });
  }, []);

  const handleEditPolicy = useCallback((item) => {
    setSelectedPolicy(item || null);
    setIsModalOpen(true);
  }, []);

  const handleModalError = useCallback(
    async (message) => {
      console.log(message);
      toast.error(message, { position: "top-center" });
      await fetchIssuedPolicies(1);
    },
    [fetchIssuedPolicies]
  );

  const handleResendLink = useCallback(async (item) => {
    if (!item?._id) {
      toast.error("Unable to identify this policy.", {
        position: "top-center",
      });
      return;
    }
    setResendingPolicyId(item._id);
    try {
      const { data } = await axiosInstance.post(
        `/api/partner/issuedinsurancepolicies/${item._id}/resend-link`
      );
      if (data?.status) {
        toast.success(data?.message || "Insurance link sent successfully.", {
          position: "top-center",
        });
      } else {
        toast.error(data?.message || "Unable to resend insurance link.", {
          position: "top-center",
        });
      }
    } catch (error) {
      console.log(error);
      toast.error(
        error?.response?.data?.message || "Unable to resend insurance link.",
        { position: "top-center" }
      );
    } finally {
      setResendingPolicyId("");
    }
  }, []);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams({
        export: "true",
        ...(debouncedSearch?.trim() && { search: debouncedSearch.trim() }),
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
        ...(isSuperAdmin && companyUid && { company_uid: companyUid }),
      });

      const { data } = await axiosInstance.get(`${listEndpoint}?${params}`);

      const policies = data?.issuedPolicies || [];
      if (policies.length === 0) {
        toast.error("No data to export for the selected range.", {
          position: "top-center",
        });
        return;
      }

      const sortedPolicies = [...policies].sort((a, b) => {
        const aTime =
          Date.parse(a?.create_date || a?.insurance_coverage_startdate || "") ||
          0;
        const bTime =
          Date.parse(b?.create_date || b?.insurance_coverage_startdate || "") ||
          0;
        return aTime - bTime;
      });

      const rows = sortedPolicies.map((item) => ({
        "Contract Number": item?.policy_number || "-",
        ...(isSuperAdmin
          ? {
              "Company Name":
                item?.company_name ||
                (item?.company_uid === TAG8_DEFAULT_COMPANY_UID
                  ? "Tag8 Default"
                  : item?.company_uid) ||
                "-",
            }
          : {}),
        "No of Bags": item?.no_of_bags ?? 1,
        "First Name": item?.first_name || "-",
        "Last Name": item?.last_name || "-",
        "Customer Name":
          [item?.first_name, item?.last_name].filter(Boolean).join(" ") || "-",
        Email: item?.email || "-",
        "Start Date": formatDate(item?.insurance_coverage_startdate),
        "Expiry Date": formatDate(item?.bag_insurance_expiry_date),
        "Created Date": formatDate(item?.create_date),
        "Created By": item?.created_by || "-",
        Status: item?.tag_status || "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Bag Protection Plan");

      const rangePart =
        fromDate && toDate
          ? `${sanitizeFileName(fromDate)}_to_${sanitizeFileName(toDate)}`
          : fromDate
          ? `from_${sanitizeFileName(fromDate)}`
          : toDate
          ? `till_${sanitizeFileName(toDate)}`
          : sanitizeFileName(todayISO());

      XLSX.writeFile(workbook, `bag_protection_plan_${rangePart}.xlsx`);
      toast.success(`${policies.length} record(s) exported successfully.`, {
        position: "top-center",
      });
    } catch {
      toast.error("Export failed. Please try again.", {
        position: "top-center",
      });
    } finally {
      setIsExporting(false);
    }
  }, [debouncedSearch, fromDate, toDate, companyUid, isSuperAdmin, listEndpoint]);

  const handleClearDates = useCallback(() => {
    setFromDate("");
    setToDate("");
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  const renderTableContent = () => {
    if (isLoadingPolicies) {
      return (
        <div className="text-center py-4 text-muted">
          Loading issued bag protection plan...
        </div>
      );
    }
    if (
      issuedPolicies.length === 0 &&
      !debouncedSearch &&
      !fromDate &&
      !toDate &&
      !companyUid
    ) {
      return (
        <div className="text-center py-4 border rounded text-muted bg-light">
          No bag protection plan issued yet
        </div>
      );
    }
    if (issuedPolicies.length === 0) {
      return (
        <div className="text-center py-4 border rounded text-muted bg-light">
          No matching plan found
        </div>
      );
    }
    return (
      <PoliciesTable
        policies={issuedPolicies}
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        resendingPolicyId={resendingPolicyId}
        onResend={handleResendLink}
        onEdit={handleEditPolicy}
        itemsPerPage={ITEMS_PER_PAGE}
        hideActions={isSuperAdmin}
        showCompany={isSuperAdmin}
      />
    );
  };

  const dateRangeActive = fromDate || toDate;

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title={isSuperAdmin ? "Admin" : "Partner Portal"}
          breadcrumbItem="Bag Protection Plan"
        />
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                  <div>
                    <h4 className="mb-1">Bag Protection Plan</h4>
                    <p className="text-muted mb-0">
                      {isSuperAdmin
                        ? "View all company bag protection plans and issue a Tag8 default policy."
                        : "Create a new Bag Protection Plan for your customers."}
                    </p>
                  </div>
                  <Button color="primary" onClick={handleOpenCreateModal}>
                    Issue Bag Protection
                  </Button>
                </div>

                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h5 className="mb-0">Issued Bag Protection Plan</h5>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {isSuperAdmin && (
                        <Input
                          type="select"
                          value={companyUid}
                          onChange={(e) => setCompanyUid(e.target.value)}
                          style={{ minWidth: "220px" }}
                          title="Filter by company name"
                        >
                          <option value="">All Companies</option>
                          <option value={TAG8_DEFAULT_COMPANY_UID}>
                            Tag8 Default
                          </option>
                          {companies.map((company) => (
                            <option
                              key={company.company_uid}
                              value={company.company_uid}
                            >
                              {company.company_name}
                            </option>
                          ))}
                        </Input>
                      )}
                      <div style={{ minWidth: "200px" }}>
                        <Input
                          type="search"
                          placeholder="Search policies"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>

                      <Input
                        type="date"
                        bsSize="md"
                        value={fromDate}
                        max={toDate || todayISO()}
                        onChange={(e) => setFromDate(e.target.value)}
                        style={{ width: "150px" }}
                        title="From date (Created Date)"
                      />
                      <Input
                        type="date"
                        bsSize="md"
                        value={toDate}
                        min={fromDate || undefined}
                        max={todayISO()}
                        onChange={(e) => setToDate(e.target.value)}
                        style={{ width: "150px" }}
                        title="To date (Created Date)"
                      />

                      {dateRangeActive && (
                        <Button
                          color="secondary"
                          outline
                          size="sm"
                          onClick={handleClearDates}
                          title="Clear date filter"
                        >
                          Clear
                        </Button>
                      )}

                      <Button
                        color="success"
                        disabled={
                          isExporting ||
                          isLoadingPolicies ||
                          !(fromDate && toDate)
                        }
                        onClick={handleExport}
                        title={
                          fromDate && toDate
                            ? "Export filtered results"
                            : "Select both From and To dates to export"
                        }
                      >
                        {isExporting ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Exporting...
                          </>
                        ) : (
                          "Export"
                        )}
                      </Button>

                      <Button
                        color="light"
                        className="border"
                        onClick={() => fetchIssuedPolicies(currentPage)}
                        disabled={isLoadingPolicies}
                      >
                        Refresh
                      </Button>
                    </div>
                  </div>

                  {dateRangeActive && !isLoadingPolicies && (
                    <p className="text-muted small mb-2">
                      {totalRecords} record(s) match the selected date range.
                    </p>
                  )}

                  {renderTableContent()}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <IssuePolicyModal
          isOpen={isModalOpen}
          toggle={handleToggleModal}
          onSuccess={handleModalSuccess}
          axiosInstance={axiosInstance}
          onError={handleModalError}
          policyToEdit={isSuperAdmin ? null : selectedPolicy}
        />
      </Container>
    </div>
  );
};

export default IssueBagProtectionPolicy;
